import { Box, Stack, TableCell } from "@mui/material";
import { flexRender, type Header } from "@tanstack/react-table";
import clsx from "clsx";
import React from "react";

interface HyperTableHeaderCellProps<Entity> {
  header: Header<Entity, unknown>;
  index: number;
  allHeaders: Header<Entity, unknown>[];
}

export const HyperTableHeaderCell = <Entity extends any>(
  props: HyperTableHeaderCellProps<Entity>,
) => {
  const { allHeaders, header, index } = props;

  const isPinned = header.column.getIsPinned();
  const leftOffset =
    isPinned && index !== 0
      ? allHeaders
          .slice(0, index)
          .filter((col) => col.column.getIsPinned() === isPinned)
          .reduce((sum, col) => sum + (col.column.getSize() || 0), -100)
      : 0;

  const canResize = header.column.getCanResize();
  const columnSize = header.getSize();
  const isResizing = header.column.getIsResizing();

  function handleMouseOrTouchDown(
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) {
    header.getResizeHandler(document)(event);
  }

  return (
    <TableCell
      sx={{
        width: columnSize,
        minWidth: columnSize,
        maxWidth: columnSize,
        overflow: "visible",
        position: isPinned ? "sticky" : "relative",
        paddingInlineEnd: canResize ? 1.5 : 0.5,
        ...(isPinned
          ? {
              left: isPinned === "left" ? leftOffset : "unset",
              right: isPinned === "right" ? leftOffset : "unset",
              background: "white",
              zIndex: 2,
            }
          : {}),
        transition: isResizing
          ? "none"
          : "width 0.2s ease-out, min-width 0.2s ease-out, max-width 0.2s ease-out",
      }}
      className={clsx(
        header.column.columnDef.headerClassName,
        "table-head-cell",
      )}
    >
      {!header.isPlaceholder && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ minWidth: 0, overflow: "hidden", width: "100%" }}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
          </Stack>
          {canResize && (
            <Box
              onMouseDown={handleMouseOrTouchDown}
              onTouchStart={handleMouseOrTouchDown}
              className={clsx("resizer", {
                "is-resizing": isResizing,
              })}
              title="גרור לשינוי רוחב העמודה"
              sx={{
                position: "absolute",
                insetInlineEnd: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: "col-resize",
                zIndex: 3,
                display: "flex",
                justifyContent: "center",
                userSelect: "none",
                touchAction: "none",
                "&::after": {
                  content: '""',
                  width: "1px",
                  height: "40%",
                  alignSelf: "center",
                  backgroundColor: "transparent",
                },
                "&:hover::after": {
                  backgroundColor: "grey.400",
                },
                "&.is-resizing::after": {
                  backgroundColor: "grey.500",
                  height: "55%",
                },
              }}
            />
          )}
        </>
      )}
    </TableCell>
  );
};
